import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SellerAvatar from "@/components/SellerAvatar";

/**
 * BeaconSpotlight — a cinematic searchlight that sweeps across a dark
 * panel of top sellers. As the beam passes over a card, that card
 * "wakes up": the avatar glows, the username unfurls, and live stats
 * fly in. The beam never stops moving, so visibility is always rotating.
 *
 * Why this design beats a static leaderboard
 * ------------------------------------------
 * 1. Motion → fixation. The human eye is hard-wired to follow moving
 *    light. Static carousels read as ads. A swept beam reads as honor.
 * 2. Scarcity in time. Each seller is "on stage" for ~1.6s per cycle,
 *    so being on the Beacon feels rare and aspirational.
 * 3. Earned, not paid. Hover text says "tonight's beam is on…", framing
 *    visibility as something Bidorah grants, not sells.
 *
 * Pure CSS — the beam is a gradient + skew transform driven by a single
 * `--beacon-pos` custom property the parent doesn't have to manage.
 */
export default function BeaconSpotlight({ data }) {
  const headliners = data?.headliners || [];
  const top10 = data?.top_10 || [];
  const ribbonSellers = top10.length ? top10 : headliners;
  // Repeat sellers along the wall so the beam always has somewhere to land
  // even when there are fewer than the visual minimum.
  const wallSellers = ribbonSellers.length
    ? Array.from({ length: Math.max(6, ribbonSellers.length) }, (_, i) =>
        ribbonSellers[i % ribbonSellers.length]
      )
    : [];

  // Track the beam's center position so we can highlight the seller
  // currently under it. Updated at ~30fps via requestAnimationFrame —
  // cheaper than CSS animation events and works on every browser.
  const wallRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!wallRef.current || !wallSellers.length) return;
    let raf = null;
    const start = performance.now();
    const PERIOD_MS = 9000; // one full sweep across all sellers
    const tick = (t) => {
      const phase = ((t - start) % PERIOD_MS) / PERIOD_MS; // 0..1
      const idx = Math.min(
        wallSellers.length - 1,
        Math.floor(phase * wallSellers.length)
      );
      setActiveIdx(idx);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [wallSellers.length]);

  if (!headliners.length) return null;

  return (
    <section
      className="relative overflow-hidden bg-zinc-950 text-white border-y border-amber-500/20"
      data-testid="beacon-spotlight"
    >
      {/* Star field — soft, low-density, subliminal */}
      <div className="absolute inset-0 beacon-stars pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-14 sm:py-20">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline text-amber-300/90 mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
              The Beacon · {data.today}
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Tonight, the beam is on
              <br />
              <span className="text-amber-300">{data.category_label}.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/70 max-w-2xl">
              {data.tagline} The Beacon rotates daily — every seller has a
              shot at lighting up the homepage of Bidorah.
            </p>
          </div>
          <Link
            to="/hall-of-beacons"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 hover:border-amber-300 px-4 py-2 text-xs font-bold uppercase tracking-widest text-amber-300/90 hover:text-amber-200 transition-colors"
            data-testid="beacon-hall-cta"
          >
            Hall of Beacons →
          </Link>
        </div>

        {/* The wall — sellers in a row, with the beam overlaid */}
        <div
          ref={wallRef}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-zinc-900/80 to-zinc-950 border border-amber-400/10 px-2 py-8 sm:py-12"
          data-testid="beacon-wall"
        >
          {/* The searchlight beam itself — angled gradient that animates
              left to right. Pure CSS. */}
          <div className="absolute inset-0 beacon-beam pointer-events-none" />

          <div className="relative grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 sm:gap-4">
            {wallSellers.slice(0, 12).map((s, i) => {
              const active = i === activeIdx;
              return (
                <Link
                  key={`${s.user_id}-${i}`}
                  to={`/seller/${s.user_id}`}
                  data-testid={`beacon-tile-${i}`}
                  className={`group relative flex flex-col items-center justify-center rounded-xl p-3 sm:p-4 transition-all duration-500 ${
                    active
                      ? "bg-amber-300/10 ring-2 ring-amber-300/70 scale-[1.06] shadow-[0_0_40px_rgba(252,211,77,0.35)]"
                      : "bg-white/5 ring-1 ring-white/5 scale-100"
                  }`}
                >
                  <div
                    className={`relative ${
                      active ? "drop-shadow-[0_0_18px_rgba(252,211,77,0.6)]" : ""
                    }`}
                  >
                    <SellerAvatar
                      picture={s.picture}
                      name={s.username}
                      size={56}
                      className="ring-2 ring-white/20"
                    />
                    {s.rank <= 3 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-300 text-amber-950 text-[10px] font-black">
                        {s.rank}
                      </span>
                    )}
                  </div>
                  <p
                    className={`mt-2 text-xs sm:text-sm font-bold truncate max-w-full ${
                      active ? "text-amber-100" : "text-white/85"
                    }`}
                  >
                    @{s.username}
                  </p>
                  {/* Stat flies in only while the beam is overhead */}
                  <p
                    className={`mt-0.5 text-[10px] uppercase tracking-wider font-mono text-amber-300 transition-opacity duration-300 ${
                      active ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {data.category === "recruiter" && `+${s.verified_recruits} verified`}
                    {data.category === "most_loved" &&
                      `${s.avg_rating}★ · ${s.review_count} reviews`}
                    {data.category === "velocity" && `+${s.velocity_pct}% wk`}
                    {data.category === "repeat" && `${s.repeat_score} repeats`}
                    {data.category === "sell_through" &&
                      `${s.sell_through_pct}% sell-through`}
                    {data.category === "composite" && `${s.composite} pts`}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Rotation legend — shows the whole week's schedule */}
        <div className="mt-7 flex flex-wrap gap-2 items-center justify-center">
          <span className="text-[10px] uppercase tracking-widest text-white/40 mr-2">
            This week
          </span>
          {(data.rotation || []).map((r) => {
            const isToday = r.label === data.category_label;
            return (
              <span
                key={r.day}
                className={`text-[10px] sm:text-xs rounded-full px-3 py-1 border ${
                  isToday
                    ? "bg-amber-300 text-amber-950 border-amber-300 font-black"
                    : "border-white/15 text-white/60"
                }`}
              >
                {r.day} · {r.label}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}
